import { logger } from '@/utils/logger';
import { BACKEND_CONFIG } from '@/config/backendConfig';

interface AdminLoginRequest {
  username: string;
  password: string;
}

interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_superuser: boolean;
  user_type: 'admin';
}

interface AdminLoginResponse {
  access_token: string;
  refresh_token: string;
  user?: AdminUser;  // Django format
  admin?: AdminUser; // FastAPI format
}

class AdminAuthService {
  private currentUser: AdminUser | null = null;
  private baseUrl: string;

  constructor() {
    // Get base URL using same logic as main API client
    this.baseUrl = this.getApiBaseUrl();
    this.loadUser();
  }

  private getApiBaseUrl(): string {
    const hostname = window.location.hostname;
    const apiPort = BACKEND_CONFIG.apiPort;

    // If on localhost subdomain, use configured backend
    if (hostname.includes('.localhost') || hostname === 'localhost') {
      const apiPrefix = BACKEND_CONFIG.isFastAPI ? '' : '/api';
      return `http://${hostname}:${apiPort}${apiPrefix}`;
    }

    // For production, use env variable
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      return apiUrl;
    }

    // Fallback (should never be used in production)
    const apiPrefix = BACKEND_CONFIG.isFastAPI ? '' : '/api';
    return `${window.location.protocol}//${hostname}:${apiPort}${apiPrefix}`;
  }

  private loadUser(): void {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      try {
        this.currentUser = JSON.parse(userData);
      } catch (error) {
        logger.error('Failed to parse admin user data from localStorage', error);
        this.clearUser();
      }
    }
  }

  private saveUser(user: AdminUser): void {
    this.currentUser = user;
    localStorage.setItem('admin_user', JSON.stringify(user));
  }

  private clearUser(): void {
    this.currentUser = null;
    localStorage.removeItem('admin_user');
  }

  public async login(credentials: AdminLoginRequest): Promise<AdminLoginResponse> {
    try {
      const loginEndpoint = BACKEND_CONFIG.endpoints.adminLogin(BACKEND_CONFIG.isFastAPI);
      const response = await fetch(`${this.baseUrl}${loginEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.detail || 'Login failed');
      }

      const data: AdminLoginResponse = await response.json();
      const { access_token, refresh_token } = data;

      // Handle different response formats (Django: user, FastAPI: admin)
      const user = data.user || data.admin;
      if (!user) {
        throw new Error('Invalid login response format');
      }

      // Save tokens (sessionStorage) and user data (localStorage)
      sessionStorage.setItem('admin_access_token', access_token);
      sessionStorage.setItem('admin_refresh_token', refresh_token);
      this.saveUser(user);

      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Login failed');
    }
  }

  public async logout(): Promise<void> {
    const wasAuthenticated = this.isAuthenticated();
    const token = sessionStorage.getItem('admin_access_token');

    // Immediately clear local data for instant logout
    sessionStorage.removeItem('admin_access_token');
    sessionStorage.removeItem('admin_refresh_token');
    this.clearUser();

    // Make logout API call in background (don't await)
    if (wasAuthenticated && token) {
      const logoutEndpoint = BACKEND_CONFIG.endpoints.adminLogout(BACKEND_CONFIG.isFastAPI);
      fetch(`${this.baseUrl}${logoutEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }).catch(error => {
        logger.debug('Admin logout API call failed (non-critical):', error);
      });
    }
  }

  public isAuthenticated(): boolean {
    const token = sessionStorage.getItem('admin_access_token');
    return !!token && !!this.currentUser;
  }

  public getUser(): AdminUser | null {
    return this.currentUser;
  }

  public getToken(): string | null {
    return sessionStorage.getItem('admin_access_token');
  }

  public isSuperuser(): boolean {
    return this.currentUser?.is_superuser || false;
  }
}

export const adminAuthService = new AdminAuthService();
export default adminAuthService;
